import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { PayrollData } from './PayrollTypes';

interface UsePayrollDataProps {
  employeeIds: string[];
  startDate: string;
  endDate: string;
}

export const usePayrollData = ({ employeeIds, startDate, endDate }: UsePayrollDataProps) => {
  const [payrollData, setPayrollData] = useState<PayrollData[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPayrollData = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('calculate_payroll_for_period', {
        employee_ids: employeeIds,
        period_start: startDate,
        period_end: endDate
      });

      if (error) throw error;
      
      if (!data || data.length === 0) {
        toast.error('No payroll data found. Please ensure employees have wage profiles set up.');
        return;
      }
      
      setPayrollData(data || []);
    } catch (error) {
      console.error('Error fetching payroll data:', error);
      toast.error('Failed to calculate payroll data. Please check employee profiles and time entries.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeIds.length > 0 && startDate && endDate) {
      fetchPayrollData();
    }
  }, [employeeIds, startDate, endDate]);

  return {
    payrollData,
    loading,
    refetch: fetchPayrollData
  };
};